import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Filter,
  Banknote,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

export default function AdminPaymentRequests() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, paid: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const [payDialog, setPayDialog] = useState(null);
  const [payReference, setPayReference] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listPaymentRequests(
        statusFilter ? { status: statusFilter } : {}
      );
      setRows(res.data || []);
      setSummary(res.summary || { pending: 0, approved: 0, paid: 0 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = useCallback(
    async (row) => {
      setBusyId(row.paymentRequestId);
      try {
        await adminAPI.approvePaymentRequest(row.paymentRequestId);
        toast.success('Payment request approved');
        load();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to approve');
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const handleReject = useCallback(
    async (row) => {
      setBusyId(row.paymentRequestId);
      try {
        await adminAPI.rejectPaymentRequest(row.paymentRequestId, '');
        toast.success('Payment request rejected');
        load();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to reject');
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const handleMarkPaid = useCallback(async () => {
    if (!payDialog) return;
    setBusyId(payDialog.paymentRequestId);
    try {
      await adminAPI.markPaymentRequestPaid(payDialog.paymentRequestId, {
        paymentReference: payReference,
      });
      toast.success('Marked as paid');
      setPayDialog(null);
      setPayReference('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark as paid');
    } finally {
      setBusyId(null);
    }
  }, [payDialog, payReference, load]);

  const statCards = useMemo(
    () => [
      { key: 'pending', label: 'Pending', value: summary.pending, Icon: Clock, color: 'text-amber-600 bg-amber-50' },
      { key: 'approved', label: 'Approved', value: summary.approved, Icon: CheckCircle2, color: 'text-blue-600 bg-blue-50' },
      { key: 'paid', label: 'Paid', value: summary.paid, Icon: Banknote, color: 'text-green-600 bg-green-50' },
    ],
    [summary]
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900">Payment Requests</h1>
            </div>
            <p className="text-gray-500 mt-1">
              Review partner-submitted payment requests for completed services.
            </p>
          </div>
          <Button variant="outline" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {statCards.map(({ key, label, value, Icon, color }) => (
            <Card key={key} className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {label}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
            </Card>
          ))}
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                statusFilter === f.value
                  ? 'bg-purple-50 border-purple-200 text-purple-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-700 font-medium">No payment requests</p>
            <p className="text-sm text-gray-400 mt-1">
              Nothing matches the current filter.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <Card key={r.paymentRequestId} className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-lg font-semibold text-gray-900">
                        ${Number(r.amount).toFixed(2)}
                      </p>
                      <Badge className={STATUS_STYLE[r.status] || 'bg-gray-100'}>
                        {r.status}
                      </Badge>
                      {r.serviceRequest?.serviceType && (
                        <span className="text-xs text-gray-500">
                          · {r.serviceRequest.serviceType.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Partner:{' '}
                      {r.partner
                        ? `${r.partner.firstName} ${r.partner.lastName}`
                        : r.partnerId}
                      {r.partner?.email && ` · ${r.partner.email}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Service Request: {r.serviceRequestId} ·{' '}
                      {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
                      {r.paidAt && ` · paid ${format(new Date(r.paidAt), 'MMM dd, yyyy')}`}
                      {r.paymentReference && ` · ref: ${r.paymentReference}`}
                    </p>
                    {r.note && (
                      <p className="text-sm text-gray-500 italic mt-1">"{r.note}"</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {r.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(r)}
                          disabled={busyId === r.paymentRequestId}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(r)}
                          disabled={busyId === r.paymentRequestId}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      </>
                    )}
                    {['pending', 'approved'].includes(r.status) && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setPayDialog(r);
                          setPayReference('');
                        }}
                        disabled={busyId === r.paymentRequestId}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Banknote className="w-3.5 h-3.5 mr-1" /> Mark Paid
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Mark Paid Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(v) => !v && setPayDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" /> Mark as Paid
            </DialogTitle>
            <DialogDescription>
              Record the payment reference (e.g. transfer ID, Stripe charge).
              Optional.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium text-gray-700">
              Payment Reference
            </label>
            <Input
              value={payReference}
              onChange={(e) => setPayReference(e.target.value)}
              placeholder="e.g. TXN-12345"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={busyId === payDialog?.paymentRequestId}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {busyId === payDialog?.paymentRequestId ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
